import { useRef, useState, type DragEvent, type ReactNode } from 'react';

type Props = {
  onFiles: (files: FileList) => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  hint?: ReactNode;
};

// The dropzone is a <label> wrapping a hidden file input, so a click anywhere
// in it opens the picker via the browser's native label→control behaviour. An
// earlier version used a <div> with onClick={() => inputRef.current.click()};
// that programmatic .click() on a display:none input was unreliable and often
// produced no `change` event at all, so picking a file did nothing.
export function FileDrop({ onFiles, accept, multiple = true, disabled = false, hint }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  function onDrop(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    setOver(false);
    if (disabled) return;
    if (e.dataTransfer.files?.length) onFiles(e.dataTransfer.files);
  }

  return (
    <label
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      className={`block rounded-xl border-2 border-dashed p-6 text-center transition ${
        over ? 'border-blue-400 bg-blue-50' : 'border-slate-200 bg-white'
      } ${disabled ? 'opacity-50' : 'cursor-pointer hover:bg-slate-50'}`}
    >
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={accept}
        multiple={multiple}
        onChange={(e) => {
          if (e.target.files?.length) onFiles(e.target.files);
          if (inputRef.current) inputRef.current.value = '';
        }}
        disabled={disabled}
      />
      <p className="text-sm font-medium text-slate-700">
        Drop file{multiple ? 's' : ''} here or click to browse
      </p>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </label>
  );
}
